import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Alert, Badge, Row, Col, Spinner } from "react-bootstrap";
import RefineSearchSidebar from "./components/RefineSearchSidebar";
import ProfileCard from "./components/ProfileCard";
import Pagination from "./components/Pagination";
import axios from "axios";
import config from "../../../config";
import { toast } from "../../Common/Toast";
import { setUser } from "../../../features/user/userSlice";

const DNAMatches = ({ chatBoxOpen, key = null }) => {
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [dnaMatches] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasGeneticData, setHasGeneticData] = useState(false);
  const [hasCrossModalData, setHasCrossModalData] = useState(false);
  const [userGeneticData, setUserGeneticData] = useState(null);

  const profilesPerPage = 5;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const token = useSelector((state) => state.user.token);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const hasFetchedRef = useRef(false);
  const prevFiltersRef = useRef(filters);
  const prevKeyRef = useRef(key);
  const prevRefreshTriggerRef = useRef(refreshTrigger);

  const lookingFor = user?.looking_for;
  const searchFor = lookingFor === "Bride" ? "Groom" : "Bride";

  const getSoulmateLevel = (score) => {
    if (score >= 85) return "Exceptional Soulmate Match";
    if (score >= 70) return "Strong Soulmate Potential";
    if (score >= 60) return "Good Soulmate Potential";
    if (score >= 45) return "Moderate Soulmate Potential";
    return "Limited Soulmate Potential";
  };

  const fetchUserGeneticData = async () => {
    try {
      if (!token) return false;

      const response = await axios.get(`${config.baseURL}/api/dna/parsed-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setUserGeneticData(response.data.data);
        dispatch(
          setUser({
            userInfo: { ...user, genetic_data: response.data.data },
            token,
          })
        );
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const fetchDNAMatches = async () => {
    if (!user?.user_id || !token) {
      toast.error("Please log in to view DNA matches");
      return;
    }

    setLoading(true);
    try {
      const timestamp = new Date().getTime();

      // Primary: cross-modal matches endpoint (already scored server-side)
      let matches = [];
      let crossModalMode = false;
      let currentUserGenetic = null;

      try {
        const crossModalResponse = await axios.get(`${config.baseURL}/api/dna/cross-modal/matches`, {
          params: { limit: 50, stage: "soulmate", _t: timestamp, ...filters },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (crossModalResponse.data.success) {
          matches = crossModalResponse.data.matches || [];
          currentUserGenetic = crossModalResponse.data.current_user_genetic || crossModalResponse.data.currentUser?.genetic_profile;
          crossModalMode = true;
        }
      } catch (e) {
        crossModalMode = false;
      }

      // Fallback: regular matches suggestions (no scoring)
      if (!matches.length) {
        const regularResponse = await axios.get(`${config.baseURL}/api/matches/my-matches`, {
          params: { user_id: user.user_id, looking_for: searchFor, ...filters, _t: timestamp },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (regularResponse.data.success) {
          matches = regularResponse.data.users || [];
          crossModalMode = false;
        }
      }

      // Normalize match objects to your UI format
      const processed = matches.map((m) => {
        const p = m.user || m;
        const soulmateScore =
          p.soulmate_score ??
          m.soulmate_score ??
          m.crossModalCompatibility?.soulmateScore ??
          m.crossModalCompatibility?.compatibility?.soulmateScore ??
          p.compatibilityScore ??
          p.dna_compatibility_score ??
          0;

        const crossModalCompatibility = m.crossModalCompatibility || p.crossModalCompatibility || null;

        return {
          ...p,
          user_id: p.user_id || p.id || m.user_id,
          first_name: p.first_name || m.first_name || "",
          last_name: p.last_name || m.last_name || "",
          profile_image: p.profile_image || m.profile_image || p.image || "",
          location: p.location || p.city || m.city || "",
          profession: p.profession || p.occupation || m.profession || "",
          looking_for: p.looking_for || m.looking_for || searchFor,

          soulmate_score: soulmateScore,
          soulmate_level: getSoulmateLevel(soulmateScore),

          crossModalCompatibility,
          has_cross_modal_compatibility: !!crossModalCompatibility,

          // keep old fields used across the app
          dna_compatibility_score: soulmateScore,
          compatibilityScore: soulmateScore,
        };
      });

      processed.sort((a, b) => (b.soulmate_score || 0) - (a.soulmate_score || 0));

      setProfiles(processed);
      setCurrentPage(1);

      setHasGeneticData(processed.some((x) => x.genetic_data || x.has_genetic_analysis));
      setHasCrossModalData(processed.some((x) => x.has_cross_modal_compatibility));

      if (!processed.length) toast.info("You have not added any friends yet.");
      if (crossModalMode && processed.length) {
        // optional toast
      }

      // store current user genetic summary if provided
      if (currentUserGenetic) {
        setUserGeneticData((prev) => prev || currentUserGenetic);
      }
    } catch (error) {
      toast.error("Failed to load matches: " + (error.response?.data?.message || error.message));
      setProfiles([]);
      setHasGeneticData(false);
      setHasCrossModalData(false);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = () => setRefreshTrigger((p) => p + 1);
  const handleConnectClick = (id) => {
    setProfiles((prev) => prev.map((p) => (p.user_id === id ? { ...p, connectionRequest: true } : p)));
  };

  const handleUploadDNA = () => window.open("/profile?tab=dna", "_blank");

  // Reset fetch flag when dependencies change
  useEffect(() => {
    if (
      JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current) ||
      key !== prevKeyRef.current ||
      refreshTrigger !== prevRefreshTriggerRef.current
    ) {
      hasFetchedRef.current = false;
      prevFiltersRef.current = filters;
      prevKeyRef.current = key;
      prevRefreshTriggerRef.current = refreshTrigger;
    }
  }, [filters, key, refreshTrigger]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    fetchUserGeneticData();
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current || !user?.user_id || !token) return;
    hasFetchedRef.current = true;
    fetchDNAMatches();
  }, [filters, key, refreshTrigger, user?.user_id, token]);

  // Pagination logic
  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = profiles.slice(indexOfFirstProfile, indexOfLastProfile);

  const soulmateScores = profiles.map((p) => p.soulmate_score || 0);
  const soulmateMatches = profiles.filter((p) => (p.soulmate_score || 0) >= 70).length;

  return (
    <div className="p-4">
      <div className="row">
        <div className="col-md-3">
          <RefineSearchSidebar setFilters={setFilters} />
        </div>

        <div className="col-md-9">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="mb-1">💖 DNA Soulmate Compatibility Matches</h4>
              <p className="text-muted mb-0 small">
                {hasCrossModalData
                  ? "Soulmate-first scoring (Psy + Profile dominate) with family planning risk shown as a second gauge"
                  : hasGeneticData
                  ? "Genetic matches available — soulmate-first + risk gauge will improve as more DNA data is uploaded"
                  : "Upload DNA to unlock soulmate compatibility and future family risk scores"}
              </p>
            </div>

            {!userGeneticData && (
              <Button variant="primary" size="sm" onClick={handleUploadDNA} className="d-flex align-items-center">
                <i className="fas fa-upload me-2"></i>
                Upload DNA Report
              </Button>
            )}
          </div>

          {!userGeneticData && (
            <Alert variant="info" className="mb-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <i className="fas fa-heart fa-2x"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">Unlock Soulmate Match + Future Family Risk</h6>
                  <p className="mb-2 small">
                    Stage 1 focuses on soulmate compatibility (emotional + life alignment). Stage 2 shows a future family risk gauge
                    only as informational guidance for later planning.
                  </p>
                  <Button variant="primary" size="sm" onClick={handleUploadDNA} className="d-flex align-items-center">
                    <i className="fas fa-upload me-2"></i>
                    Upload DNA Report Now
                  </Button>
                </div>
              </div>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading soulmate matches...</span>
              </div>
              <p className="mt-2 text-muted">Analyzing soulmate compatibility...</p>
            </div>
          ) : (
            <div className="dan-card card border-0 shadow-sm mb-3">
              {currentProfiles.length > 0 ? (
                <>
                  {hasCrossModalData && (
                    <div className="p-3 bg-light border-bottom">
                      <div className="d-flex flex-wrap justify-content-between align-items-center">
                        <div>
                          <Badge bg="success" className="me-2">
                            💖 Soulmate-First Scoring Active
                          </Badge>
                          <small className="text-muted">{soulmateMatches} matches with strong soulmate potential (≥70/100)</small>
                        </div>
                        <small className="text-muted">Sorted by Soulmate Match</small>
                      </div>
                    </div>
                  )}

                  {currentProfiles.map((profile, index) => (
                    <div key={profile.user_id || index}>
                      <ProfileCard
                        profile={profile}
                        handleConnectClick={handleConnectClick}
                        activeIndex={activeCarouselIndex}
                        setActiveIndex={setActiveCarouselIndex}
                        chatBoxOpen={chatBoxOpen}
                        dnaMatches={dnaMatches}
                        user={user}
                        onProfileUpdate={handleProfileUpdate}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center p-5">
                  <div className="mb-3">
                    <i className="fas fa-heart fa-3x text-muted"></i>
                  </div>
                  <h5 className="text-muted">{hasGeneticData ? "No Soulmate Matches Found" : "No Matches Found"}</h5>
                  <p className="text-muted mb-3">
                    {hasGeneticData
                      ? "No strong soulmate matches found with current filters. Try adjusting your search criteria."
                      : "No matches found with current filters. Try adjusting your search criteria."}
                  </p>
                </div>
              )}
            </div>
          )}

          {profiles.length > profilesPerPage && (
            <Pagination totalProfiles={profiles.length} profilesPerPage={profilesPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
          )}

          <div className="mt-4 p-4 border rounded bg-light">
            <h6 className="d-flex align-items-center">
              <i className="fas fa-heart text-danger me-2"></i>
              How the Two-Gauge Model Works
            </h6>

            <Row className="mt-3">
              <Col md={6}>
                <div className="mb-3">
                  <h6 className="small fw-bold">💖 Gauge #1 — Soulmate Match (Stage 1)</h6>
                  <ul className="small mb-0">
                    <li><strong>45% Psy-CS:</strong> emotional & communication alignment</li>
                    <li><strong>30% Profile:</strong> values, lifestyle, goals</li>
                    <li><strong>10% MCS:</strong> light medical safety check</li>
                    <li><strong>10% Rep-CS:</strong> light reproductive feasibility</li>
                    <li><strong>5% HLA-CS:</strong> biological bonus signal</li>
                  </ul>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <h6 className="small fw-bold">👶 Gauge #2 — Future Family Risk (Stage 2)</h6>
                  <ul className="small mb-0">
                    <li><strong>Risk score:</strong> 0–100 (lower is better)</li>
                    <li><strong>Uses family-planning weighting:</strong> MCS + HLA + Rep dominate</li>
                    <li><strong>Best for:</strong> exclusive / marriage planning stage</li>
                  </ul>
                </div>
              </Col>
            </Row>

            <div className="mt-3 p-3 bg-white rounded border">
              <div className="d-flex">
                <div className="me-3">
                  <i className="fas fa-info-circle text-info"></i>
                </div>
                <div>
                  <small className="text-muted">
                    <strong>Note:</strong> Soulmate Match should guide early relationship decisions. Future Family Risk is informational and becomes most useful
                    when partners are serious and discussing kids. Always consult a qualified genetic counselor for medical decisions.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default DNAMatches;
